/**
 * Crew Detail Page
 *
 * Admin page for viewing a crew member's details and history.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getUser,
  setUserPassword,
  setArchived,
  setMembershipActive,
  resendInvite,
  type UserDetail,
} from '../../../api/users';
import { getErrorMessage } from '../../../api/client';
import { useOrganization } from '../../../context/OrganizationContext';
import { useAuth } from '../../../context/AuthContext';
import { ComposeMessageModal } from '../../../components/messages';
import { CrewNotesSection, CrewNotificationStatus } from '../../../components/crew';
import { ConfirmDialog, Modal } from '../../../components/ui';
import { ROLE_CONFIG, MEMBER_STATE } from '../../../config/statusStyles';
import { formatInTimeZone } from '../../../utils/dateFormatting';

/** Format cents to dollar display string */
function formatRate(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return 'Not set';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function CrewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { paymentTrackingEnabled, currentOrg } = useOrganization();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Membership state changes. Both live on this page and nowhere else — see
  // the Roster card below for why.
  const [pendingAction, setPendingAction] = useState<
    'archive' | 'restore' | 'deactivate' | 'reactivate' | null
  >(null);
  const [stateBusy, setStateBusy] = useState(false);

  // Resend invite state
  const [resending, setResending] = useState(false);

  // Set password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [settingPassword, setSettingPassword] = useState(false);

  // Message modal state
  const [showMessageModal, setShowMessageModal] = useState(false);

  useEffect(() => {
    if (!id) return;

    const loadUser = async () => {
      try {
        setLoading(true);
        const { user } = await getUser(id);
        setUser(user);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (!id) return;

    setSettingPassword(true);
    try {
      const result = await setUserPassword(id, password);
      toast.success(result.message);
      setPassword('');
      setConfirmPassword('');
      setShowPasswordModal(false);
    } catch (err) {
      setPasswordError(getErrorMessage(err));
    } finally {
      setSettingPassword(false);
    }
  };

  const openPasswordModal = () => {
    setPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setShowPasswordModal(true);
  };

  const handleResendInvite = async () => {
    if (!id) return;
    setResending(true);
    try {
      const result = await resendInvite(id);
      toast.success(result.message);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  /**
   * Apply an archive/deactivate state change and refresh the page in place.
   *
   * Deliberately stays on the profile afterwards rather than bouncing back to
   * the list: the admin needs to see that the state actually changed, and for
   * deactivation, how many assignments got cancelled as a result.
   */
  const applyStateChange = async () => {
    if (!id || !pendingAction) return;
    setStateBusy(true);
    try {
      // Only the deactivate path reports cancelled assignments, so it is the
      // only one that can add that clause.
      let message: string;
      if (pendingAction === 'archive' || pendingAction === 'restore') {
        ({ message } = await setArchived(id, pendingAction === 'archive'));
      } else {
        const result = await setMembershipActive(id, pendingAction === 'reactivate');
        const cancelled = result.cancelledAssignments ?? 0;
        message =
          cancelled > 0
            ? `${result.message}. ${cancelled} open ${
                cancelled === 1 ? 'assignment' : 'assignments'
              } cancelled.`
            : result.message;
      }
      toast.success(message);

      const { user: refreshed } = await getUser(id);
      setUser(refreshed);
      setPendingAction(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setPendingAction(null);
    } finally {
      setStateBusy(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return formatInTimeZone(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-20 h-20 bg-gray-200"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'User not found'}</div>
        <Link to="/admin/crew" className="text-primary-600 hover:text-primary-500">
          Back to Crew List
        </Link>
      </div>
    );
  }

  const roleInfo = ROLE_CONFIG[user.role];
  const memberState = user.memberState ?? 'ACTIVE';
  const stateInfo = MEMBER_STATE[memberState];
  const isSelf = user.id === currentUser?.id;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/admin/crew" className="text-gray-500 hover:text-gray-700">
              Crew
            </Link>
          </li>
          <li>
            <span className="text-gray-400">/</span>
          </li>
          <li className="text-gray-900 font-medium">
            {user.firstName} {user.lastName}
          </li>
        </ol>
      </nav>

      {/* Header with actions */}
      <div className="mb-8 flex items-start justify-between">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          {user.profilePhoto ? (
            <img
              src={user.profilePhoto}
              alt={`${user.firstName} ${user.lastName}`}
              className="w-20 h-20 object-cover"
            />
          ) : (
            <div className="w-20 h-20 bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-semibold">
              {getInitials(user.firstName, user.lastName)}
            </div>
          )}

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user.firstName} {user.lastName}
            </h1>
            <p className="text-gray-600">{user.email}</p>
            <div className="flex gap-2 mt-2">
              <span className={`px-2.5 py-0.5 text-xs font-medium ${roleInfo.className}`}>
                {roleInfo.label}
              </span>
              {memberState !== 'ACTIVE' && (
                <span className={`px-2.5 py-0.5 text-xs font-medium ${stateInfo.className}`}>
                  {stateInfo.label}
                </span>
              )}
              {/* Platform-wide account state. An org admin cannot set this —
                  it is shown so "why can't they log in" has an answer. */}
              {user.status === 'INACTIVE' && (
                <span className="px-2.5 py-0.5 text-xs font-medium border border-program bg-white text-program-dark">
                  Account disabled
                </span>
              )}
              {memberState === 'ACTIVE' && user.status !== 'INACTIVE' && !user.lastLoginAt && (
                <span className="px-2.5 py-0.5 text-xs font-medium border border-maybe bg-white text-maybe-dark">
                  Never logged in
                </span>
              )}
            </div>
          </div>
        </div>

        <Link
          to={`/admin/crew/${user.id}/edit`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <a href={`mailto:${user.email}`} className="text-primary-600 hover:text-primary-500">
                    {user.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {user.phone ? (
                    <a href={`tel:${user.phone}`} className="text-primary-600 hover:text-primary-500">
                      {user.phone}
                    </a>
                  ) : (
                    <span className="text-gray-400">Not provided</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Member Since</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(user.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(user.lastLoginAt)}</dd>
              </div>
            </dl>
          </div>

          {/* Admin Notes (never visible to the crew member) */}
          <CrewNotesSection userId={user.id} />

          {/* Notification settings — read-only; answers "I never got that message" */}
          <CrewNotificationStatus userId={user.id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Assignments</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {user.assignmentCount ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Availability Responses</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {user.availabilityCount ?? 0}
                </dd>
              </div>
            </dl>
          </div>

          {/* Pay Rates (only when payment tracking enabled) */}
          {paymentTrackingEnabled && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pay Rates</h2>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Hourly Rate</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900">
                    {formatRate(user.defaultHourlyRate)}
                    {user.defaultHourlyRate && <span className="text-sm font-normal text-gray-500">/hr</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Daily/Flat Rate</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900">
                    {formatRate(user.defaultDailyRate)}
                    {user.defaultDailyRate && <span className="text-sm font-normal text-gray-500">/day</span>}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {!user.lastLoginAt && user.status !== 'INACTIVE' && (
                <button
                  onClick={handleResendInvite}
                  disabled={resending}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {resending ? 'Sending...' : 'Resend Invitation'}
                </button>
              )}
              <button
                onClick={openPasswordModal}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Set Password
              </button>
              <button
                onClick={() => setShowMessageModal(true)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Message
              </button>
            </div>
          </div>

          {/* Roster & Access
              ---------------
              Archive and deactivate live here, on the profile, and nowhere
              else. Both are decisions about one person that need this page's
              context — when they last signed in, what they are still assigned
              to — and neither belongs on a row in a list. Both are per-org:
              nothing here reaches the other organizations a freelancer crews
              for. Never offered for yourself; the server refuses either way,
              and there would be nobody left to undo it. */}
          {!isSelf && (
            <div className="bg-white border border-hairline p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Roster &amp; Access</h2>
              <p className="text-sm text-ink-secondary mb-4">
                Both apply to {currentOrg?.orgName ?? 'this organization'} only.
              </p>

              {memberState === 'ARCHIVED' && (
                <div className="mb-4 border-l-[3px] border-l-hairline bg-paper px-3 py-2 text-sm text-ink-body">
                  Archived {user.archivedAt ? formatDate(user.archivedAt) : ''}
                  {user.archivedReason === 'AUTO_NO_RESPONSE'
                    ? ' automatically, after no response to recent events.'
                    : user.archivedByName
                    ? ` by ${user.archivedByName}.`
                    : '.'}{' '}
                  They can still sign in and respond, and responding puts them back on the roster.
                </div>
              )}

              {memberState === 'DEACTIVATED' && (
                <div className="mb-4 border-l-[3px] border-l-ink-muted bg-paper px-3 py-2 text-sm text-ink-body">
                  Deactivated {user.deactivatedAt ? formatDate(user.deactivatedAt) : ''}. They cannot
                  reach this organization. Their history is untouched, so reports still include them.
                </div>
              )}

              <div className="space-y-2">
                {memberState === 'ACTIVE' && user.role === 'CREW' && (
                  <button
                    onClick={() => setPendingAction('archive')}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-paper flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archive
                  </button>
                )}

                {memberState === 'ARCHIVED' && (
                  <button
                    onClick={() => setPendingAction('restore')}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-paper flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Restore to roster
                  </button>
                )}

                {memberState === 'DEACTIVATED' ? (
                  <button
                    onClick={() => setPendingAction('reactivate')}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-paper flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Reactivate access
                  </button>
                ) : (
                  <button
                    onClick={() => setPendingAction('deactivate')}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Recent Assignments. Stacked rather than the two-column row the
              main column used — this is a third-width sidebar, and name +
              date + status side by side wraps into a mess at that width. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Assignments</h2>
            {user.assignments && user.assignments.length > 0 ? (
              <ul className="space-y-3">
                {user.assignments.map((assignment) => (
                  <li
                    key={assignment.id}
                    className="py-2 border-b border-gray-100 last:border-0 last:pb-0"
                  >
                    <Link
                      to={`/admin/events/${assignment.event.id}`}
                      className="block font-medium text-gray-900 hover:text-primary-600"
                    >
                      {assignment.event.name}
                    </Link>
                    <p className="text-sm text-gray-500">
                      {assignment.role.roleName} • {assignment.event.venue}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-xs">
                      <span className="text-gray-900">
                        {formatInTimeZone(assignment.event.eventDate, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span
                        className={`font-medium ${
                          assignment.status === 'CONFIRMED'
                            ? 'text-green-600'
                            : assignment.status === 'DECLINED'
                            ? 'text-red-600'
                            : 'text-yellow-600'
                        }`}
                      >
                        {assignment.status}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No assignments yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Set Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        size="md"
        labelledBy="set-password-title"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="set-password-title" className="text-lg font-semibold text-gray-900">
            Set Password for {user.firstName} {user.lastName}
          </h3>
        </div>

        <form onSubmit={handleSetPassword} className="p-6">
          {passwordError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {passwordError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                id="password"
                autoFocus
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
                placeholder="Enter new password"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
                placeholder="Confirm new password"
                required
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowPasswordModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={settingPassword}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={settingPassword}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {settingPassword ? 'Setting...' : 'Set Password'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Send Message Modal */}
      <ComposeMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        onSuccess={() => {}}
        preselectedUsers={[{ id: user.id, name: `${user.firstName} ${user.lastName}` }]}
      />

      {/* Archive / deactivate confirmation. One dialog, four messages — the
          wording is the only thing that differs, and keeping them side by side
          is what stops archive's copy from drifting toward deactivate's. */}
      <ConfirmDialog
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={applyStateChange}
        title={
          pendingAction === 'archive'
            ? 'Archive crew member'
            : pendingAction === 'restore'
            ? 'Restore to roster'
            : pendingAction === 'reactivate'
            ? 'Reactivate access'
            : 'Deactivate crew member'
        }
        message={
          pendingAction === 'archive'
            ? `Archive ${user.firstName} ${user.lastName}? They keep their account and all their history, and can still sign in and set availability. They come off your roster, out of event notices and out of the assignment picker until they respond, which restores them automatically.`
            : pendingAction === 'restore'
            ? `Put ${user.firstName} ${user.lastName} back on the active roster? They will appear in the assignment picker and start receiving event notices again.`
            : pendingAction === 'reactivate'
            ? `Restore ${user.firstName} ${user.lastName}'s access to this organization? Assignments cancelled when they were deactivated are not restored, so re-assign them to anything they should be working.`
            : `Deactivate ${user.firstName} ${user.lastName}? They lose access to this organization and their open assignments here are cancelled. Their history stays, so reports are unchanged, and nothing about their other organizations is affected. You can reactivate them later.`
        }
        confirmLabel={
          pendingAction === 'archive'
            ? 'Archive'
            : pendingAction === 'restore'
            ? 'Restore'
            : pendingAction === 'reactivate'
            ? 'Reactivate'
            : 'Deactivate'
        }
        isLoading={stateBusy}
        variant={pendingAction === 'deactivate' ? 'danger' : 'default'}
      />
    </div>
  );
}
